/**
 * Палитры объектов доски. Цвета хранятся в документе как ключ палитры
 * (например `sticky:yellow`) или как произвольный hex — так одна и та же доска
 * корректно выглядит и в светлой, и в тёмной теме.
 */

export interface PaletteColor {
  key: string;
  label: string;
  light: string;
  dark: string;
}

export const STICKY_COLORS = [
  { key: 'yellow', label: 'Жёлтый', light: '#FFF1A8', dark: '#F2DE7E' },
  { key: 'peach', label: 'Персиковый', light: '#FFD9B8', dark: '#F0C29A' },
  { key: 'coral', label: 'Коралловый', light: '#FFC2B4', dark: '#EEA898' },
  { key: 'pink', label: 'Розовый', light: '#FFC9DE', dark: '#EFAFC8' },
  { key: 'lavender', label: 'Лавандовый', light: '#E5D4FF', dark: '#CDB8F2' },
  { key: 'lilac', label: 'Сиреневый', light: '#D6D3FF', dark: '#BDB8F2' },
  { key: 'sky', label: 'Голубой', light: '#C7E4FF', dark: '#A9CDF0' },
  { key: 'teal', label: 'Бирюзовый', light: '#BDEDEA', dark: '#9ADAD5' },
  { key: 'mint', label: 'Мятный', light: '#C9F2D4', dark: '#A8E0B7' },
  { key: 'lime', label: 'Лаймовый', light: '#E3F5B0', dark: '#CDE491' },
  { key: 'sand', label: 'Песочный', light: '#EFE3CF', dark: '#DCCCB0' },
  { key: 'gray', label: 'Серый', light: '#E7E5E4', dark: '#CFCBC8' },
] as const satisfies readonly PaletteColor[];

export type StickyColor = (typeof STICKY_COLORS)[number]['key'];
export const STICKY_COLOR_KEYS = STICKY_COLORS.map((c) => c.key) as [StickyColor, ...StickyColor[]];

/** Палитра для фигур, текста, линий, пера. */
export const INK_COLORS = [
  { key: 'ink', label: 'Графит', light: '#1C1917', dark: '#F5F5F4' },
  { key: 'gray', label: 'Серый', light: '#78716C', dark: '#A8A29E' },
  { key: 'silver', label: 'Светло-серый', light: '#D6D3D1', dark: '#57534E' },
  { key: 'white', label: 'Белый', light: '#FFFFFF', dark: '#292524' },
  { key: 'red', label: 'Красный', light: '#E5484D', dark: '#FF6369' },
  { key: 'orange', label: 'Оранжевый', light: '#F76B15', dark: '#FF8B3E' },
  { key: 'amber', label: 'Янтарный', light: '#FFB224', dark: '#FFCA16' },
  { key: 'green', label: 'Зелёный', light: '#30A46C', dark: '#3DD68C' },
  { key: 'teal', label: 'Бирюзовый', light: '#12A594', dark: '#0BD8B6' },
  { key: 'blue', label: 'Синий', light: '#0090FF', dark: '#3B9EFF' },
  { key: 'violet', label: 'Фиолетовый', light: '#6E56CF', dark: '#9E8CFC' },
  { key: 'pink', label: 'Розовый', light: '#D6409F', dark: '#F76BCD' },
] as const satisfies readonly PaletteColor[];

export type InkColor = (typeof INK_COLORS)[number]['key'];

/** Мягкие заливки для фигур и фреймов. */
export const FILL_COLORS = [
  { key: 'none', label: 'Без заливки', light: 'transparent', dark: 'transparent' },
  { key: 'white', label: 'Белый', light: '#FFFFFF', dark: '#2A2725' },
  ...STICKY_COLORS.map((c) => ({ key: c.key, label: c.label, light: c.light, dark: c.dark })),
  { key: 'ink', label: 'Графит', light: '#1C1917', dark: '#E7E5E4' },
] as const satisfies readonly PaletteColor[];

/** Цвета курсоров участников. */
export const CURSOR_COLORS = [
  '#6E56CF',
  '#E5484D',
  '#F76B15',
  '#30A46C',
  '#0090FF',
  '#D6409F',
  '#12A594',
  '#AB4ABA',
  '#E54666',
  '#3E63DD',
] as const;

export type ThemeName = 'light' | 'dark';

const paletteIndex = new Map<string, PaletteColor>();
for (const c of STICKY_COLORS) paletteIndex.set(`sticky:${c.key}`, c);
for (const c of INK_COLORS) paletteIndex.set(`ink:${c.key}`, c);
for (const c of FILL_COLORS) paletteIndex.set(`fill:${c.key}`, c);

/**
 * Превращает цвет из документа в CSS-цвет для заданной темы.
 * Поддерживает ключи палитры (`ink:blue`), hex и `transparent`.
 */
export function resolveColor(value: string | null | undefined, theme: ThemeName = 'light'): string {
  if (!value) return 'transparent';
  const p = paletteIndex.get(value);
  if (p) return theme === 'dark' ? p.dark : p.light;
  return value;
}

export function stickyColor(key: string, theme: ThemeName = 'light'): string {
  return resolveColor(`sticky:${key}`, theme) === 'transparent'
    ? STICKY_COLORS[0].light
    : resolveColor(`sticky:${key}`, theme);
}

/** Детерминированный цвет из строки (для аватаров и курсоров). */
export function colorFromString(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
  return CURSOR_COLORS[Math.abs(h) % CURSOR_COLORS.length]!;
}

/** Hex → число для PixiJS. */
export function hexToNumber(hex: string): number {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((ch) => ch + ch).join('') : h.slice(0, 6);
  const n = Number.parseInt(full, 16);
  return Number.isNaN(n) ? 0 : n;
}

/** Относительная яркость — для выбора контрастного цвета текста. */
export function isDarkColor(hex: string): boolean {
  if (!hex.startsWith('#')) return false;
  const n = hexToNumber(hex);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 140;
}

/**
 * Цвет текста поверх заливки: ключ по умолчанию `ink:ink` подстраивается под яркость
 * заливки (на пастельных фигурах в тёмной теме текст остаётся тёмным).
 */
export function textOnFill(textColor: string | null | undefined, fill: string | null | undefined, theme: ThemeName): string {
  const fillCss = resolveColor(fill, theme);
  if ((textColor ?? 'ink:ink') === 'ink:ink' && fillCss.startsWith('#')) {
    return isDarkColor(fillCss) ? '#F5F5F4' : '#1C1917';
  }
  return resolveColor(textColor ?? 'ink:ink', theme);
}
